import type { UseAudioSettingsReturn } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import { useSoftphoneDevices } from 'Modules/softphone/hooks';
import {
  useSoftphoneCoordinatorStore,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import { audioSettingsLogger as logger } from 'Modules/softphone/utils/logger';
import { useCallback, useContext, useEffect, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';

export type { UseAudioSettingsReturn } from './index.types';

/**
 * useAudioSettings Hook
 *
 * Single unified hook that manages audio device settings for both legacy and new softphone.
 * Encapsulates all audio settings logic and provides a consistent interface.
 *
 * This hook automatically switches between:
 * - NEW softphone: Uses Zustand store (useSoftphoneDevices)
 * - LEGACY softphone: Uses MobX store (PhoneStore)
 *
 * The component using this hook doesn't need to know which implementation is active.
 */
export const useAudioSettings = (): UseAudioSettingsReturn => {
  // Get MobX context (always available for legacy)
  const mobxContext = useContext(MobXProviderContext) as RootStoreProps | null;
  const phoneCall = mobxContext?.phoneCall;
  const personId = mobxContext?.person?.loggedInPersonId;

  // Get softphone preference from coordinator
  const softphoneCoordinatorStore = useSoftphoneCoordinatorStore(personId);
  const isNewSoftphoneEnabled = softphoneCoordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  logger.debug('useAudioSettings', {
    isNewSoftphoneEnabled,
    personId,
    hasPhoneCall: !!phoneCall,
  });

  // NEW softphone hooks (only used when enabled)
  const newDevices = useSoftphoneDevices();
  const newStore = useSoftphoneStore((state) => ({
    permissionState: state.permissionState,
    selectedRinger: state.selectedRinger,
  }));

  // Local state for test microphone (NEW softphone only)
  const [testMicrophoneEnabled, setTestMicrophoneEnabled] = useState(false);

  // Initialize devices on mount
  useEffect(() => {
    logger.info('useEffect triggered', {
      isNewSoftphoneEnabled,
      hasPhoneCall: !!phoneCall,
    });

    let handleDeviceChange: (() => void) | null = null;

    if (isNewSoftphoneEnabled) {
      // NEW softphone initialization
      logger.info('Initializing NEW softphone audio settings');
      void newDevices.refreshDevices();

      handleDeviceChange = () => {
        logger.debug('Device change detected (NEW softphone)');
        void newDevices.refreshDevices();
      };

      navigator.mediaDevices?.addEventListener(
        'devicechange',
        handleDeviceChange
      );
      logger.debug('Device change listener added (NEW softphone)');
    } else if (phoneCall) {
      // LEGACY softphone initialization (only if phoneCall is available)
      logger.info('Initializing LEGACY softphone audio settings', {
        microphoneOptions: phoneCall.microphoneOptions.length,
        speakerOptions: phoneCall.speakerOptions.length,
        browserPermissionState: phoneCall.browserMicrophonePermissionState,
      });

      // Request microphone permission and enumerate devices
      void phoneCall.refreshMicrophonePermissionStatus(null);

      // Force reload devices from localforage to ensure persisted values are loaded
      // This is necessary because the legacy PhoneStore may have already initialized
      // with default values before we mounted this component
      phoneCall.setIsThereDeviceChange(true);
      phoneCall.checkForIODevices();

      handleDeviceChange = () => {
        logger.debug('Device change detected (LEGACY softphone)');
        phoneCall.setIsThereDeviceChange(true);
        phoneCall.checkForIODevices();
      };

      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange
      );
      logger.debug('Device change listener added (LEGACY softphone)');
    } else {
      logger.warn('LEGACY softphone selected but phoneCall is not available');
    }

    // Cleanup function always runs, even if phoneCall was undefined
    return () => {
      logger.debug('Cleanup triggered', {
        hasListener: !!handleDeviceChange,
        isNewSoftphoneEnabled,
      });

      if (handleDeviceChange) {
        navigator.mediaDevices?.removeEventListener(
          'devicechange',
          handleDeviceChange
        );
        logger.debug('Device change listener removed');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewSoftphoneEnabled, phoneCall]);

  // Handlers
  const handleRefreshClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      logger.info('Refresh button clicked', { isNewSoftphoneEnabled });

      const element = e.target as HTMLElement;
      if (isNewSoftphoneEnabled) {
        logger.debug('Refreshing devices (NEW softphone)');
        void newDevices.refreshDevices();
      } else {
        if (phoneCall) {
          logger.debug('Refreshing microphone permissions (LEGACY softphone)', {
            currentMicOptions: phoneCall.microphoneOptions.length,
            currentSpeakerOptions: phoneCall.speakerOptions.length,
          });
          void phoneCall.refreshMicrophonePermissionStatus(null);
        } else {
          logger.warn('Cannot refresh: phoneCall not available');
        }
      }
      element.classList.add('blink_me');
      setTimeout(() => {
        element.classList.remove('blink_me');
      }, 1000);
    },
    [isNewSoftphoneEnabled, newDevices, phoneCall]
  );

  const handleMicrophoneChange = useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
      const deviceId = data.value as string;
      logger.info('Microphone changed', { deviceId, isNewSoftphoneEnabled });

      if (isNewSoftphoneEnabled) {
        void newDevices.selectMicrophone(deviceId);
      } else {
        if (phoneCall) {
          // Find the option in the existing list to get the correct key
          const existingOption = phoneCall.microphoneOptions.find(
            (opt) => opt.value === deviceId
          );

          // Use existing option or create one with index-based key
          const option = existingOption || {
            key: phoneCall.microphoneOptions.length,
            text: data.text || deviceId,
            value: deviceId,
          };

          phoneCall.setTheMic(event, option);
        } else {
          logger.error('Cannot change microphone: phoneCall not available');
        }
      }
    },
    [isNewSoftphoneEnabled, newDevices, phoneCall]
  );

  const handleSpeakerChange = useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
      const deviceId = data.value as string;
      logger.info('Speaker changed', { deviceId, isNewSoftphoneEnabled });

      if (isNewSoftphoneEnabled) {
        void newDevices.selectSpeaker(deviceId);
      } else {
        if (phoneCall) {
          // Find the option in the existing list to get the correct key
          const existingOption = phoneCall.speakerOptions.find(
            (opt) => opt.value === deviceId
          );

          // Use existing option or create one with index-based key
          const option = existingOption || {
            key: phoneCall.speakerOptions.length,
            text: data.text || deviceId,
            value: deviceId,
          };

          phoneCall.setTheSpeaker(event, option);
        } else {
          logger.error('Cannot change speaker: phoneCall not available');
        }
      }
    },
    [isNewSoftphoneEnabled, newDevices, phoneCall]
  );

  const handleRingerChange = useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
      const deviceId = data.value as string;
      logger.info('Ringer changed', { deviceId, isNewSoftphoneEnabled });

      if (isNewSoftphoneEnabled) {
        void newDevices.selectRinger(deviceId);
      } else {
        if (phoneCall) {
          // Find the option in the existing list to get the correct key
          const existingOption = phoneCall.speakerOptions.find(
            (opt) => opt.value === deviceId
          );

          // Use existing option or create one with index-based key
          const option = existingOption || {
            key: phoneCall.speakerOptions.length,
            text: data.text || deviceId,
            value: deviceId,
          };

          phoneCall.setRingSpeaker(event, option);
        } else {
          logger.error('Cannot change ringer: phoneCall not available');
        }
      }
    },
    [isNewSoftphoneEnabled, newDevices, phoneCall]
  );

  const toggleTestMicrophone = useCallback(() => {
    logger.debug('Test microphone toggled', { isNewSoftphoneEnabled });

    if (isNewSoftphoneEnabled) {
      setTestMicrophoneEnabled((prev) => {
        logger.debug('Test microphone state changed (NEW)', {
          newState: !prev,
        });
        return !prev;
      });
    } else {
      if (phoneCall) {
        phoneCall.toggleTestMicrophoneEnabled();
        logger.debug('Test microphone toggled (LEGACY)');
      } else {
        logger.error('Cannot toggle test microphone: phoneCall not available');
      }
    }
  }, [isNewSoftphoneEnabled, phoneCall]);

  // Prepare unified interface based on active softphone
  if (isNewSoftphoneEnabled) {
    // NEW softphone data
    const microphoneOptions = newDevices.microphones.map((device) => ({
      key: device.deviceId,
      text: device.label || 'Unknown Microphone',
      value: device.deviceId,
    }));

    const speakerOptions = newDevices.speakers.map((device) => ({
      key: device.deviceId,
      text: device.label || 'Unknown Speaker',
      value: device.deviceId,
    }));

    logger.debug('Returning NEW softphone interface', {
      microphoneCount: microphoneOptions.length,
      speakerCount: speakerOptions.length,
      selectedMicrophone: newDevices.selectedMicrophone,
      selectedSpeaker: newDevices.selectedSpeaker,
      selectedRinger: newStore.selectedRinger,
      permissionState: newStore.permissionState,
    });

    return {
      microphoneOptions,
      speakerOptions,
      ringerOptions: speakerOptions, // Ringers use same devices as speakers
      selectedMicrophone: newDevices.selectedMicrophone || undefined,
      selectedSpeaker: newDevices.selectedSpeaker || undefined,
      selectedRinger: newStore.selectedRinger || undefined,
      handleMicrophoneChange,
      handleSpeakerChange,
      handleRingerChange,
      handleRefreshClick,
      toggleTestMicrophone,
      permissionDenied: newStore.permissionState === 'denied',
      testMicrophoneEnabled,
      isUsingNewSoftphone: true,
    };
  } else {
    // LEGACY softphone data
    if (!phoneCall) {
      logger.warn('Returning empty interface: phoneCall not available');

      // Fallback if phoneCall is not available
      return {
        microphoneOptions: [],
        speakerOptions: [],
        ringerOptions: [],
        selectedMicrophone: undefined,
        selectedSpeaker: undefined,
        selectedRinger: undefined,
        handleMicrophoneChange,
        handleSpeakerChange,
        handleRingerChange,
        handleRefreshClick,
        toggleTestMicrophone,
        permissionDenied: false,
        testMicrophoneEnabled: false,
        isUsingNewSoftphone: false,
      };
    }

    logger.debug('Returning LEGACY softphone interface', {
      microphoneCount: phoneCall.microphoneOptions.length,
      speakerCount: phoneCall.speakerOptions.length,
      selectedMicrophone: phoneCall.theDefaultMic,
      selectedSpeaker: phoneCall.theDefaultSpeaker,
      selectedRinger: phoneCall.theDefaultRingDevice,
      permissionState: phoneCall.browserMicrophonePermissionState,
    });

    return {
      microphoneOptions: phoneCall.microphoneOptions,
      speakerOptions: phoneCall.speakerOptions,
      ringerOptions: phoneCall.speakerOptions, // Ringers use same devices as speakers
      selectedMicrophone: phoneCall.theDefaultMic,
      selectedSpeaker: phoneCall.theDefaultSpeaker,
      selectedRinger: phoneCall.theDefaultRingDevice,
      handleMicrophoneChange,
      handleSpeakerChange,
      handleRingerChange,
      handleRefreshClick,
      toggleTestMicrophone,
      permissionDenied: phoneCall.browserMicrophonePermissionState === 'denied',
      testMicrophoneEnabled: phoneCall.testMicrophoneEnabled,
      isUsingNewSoftphone: false,
    };
  }
};
